import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import confetti from 'canvas-confetti'
import { Check, Link2Off } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { isTokenExpired } from '../lib/guestToken'

// ─── Types ────────────────────────────────────────────────────────────────────

interface GuestSession {
  id:          string
  household_id: string
  list_id:     string
  token:       string
  expires_at:  string
  used_count:  number
}

interface GuestListItem {
  id:          string
  status:      string
  sort_order:  number
  pantry_items: {
    id:               string
    name:             string
    display_category: string
    location:         string | null
  } | null
}

interface FlatGuestItem {
  id:               string
  status:           string
  sort_order:       number
  name:             string
  display_category: string
  location:         string | null
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_ORDER = [
  'Produce', 'Dairy', 'Meat', 'Bakery',
  'Pantry', 'Cleaning', 'Personal Care', 'Other',
]

// Location pill: subtle warm-editorial tints, no emoji on data.
const LOC_TINT: Record<string, string> = {
  fridge:   'bg-[#EAF1F4] text-[#37607A]',
  freezer:  'bg-[#EBEFF4] text-[#3A4A6E]',
  cabinets: 'bg-cream-warm text-terracotta',
  other:    'bg-paper text-ink-muted border border-border-sand',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function flattenGuestItems(raw: GuestListItem[]): FlatGuestItem[] {
  return raw.map(row => ({
    id:               row.id,
    status:           row.status,
    sort_order:       row.sort_order,
    name:             row.pantry_items?.name             ?? 'Unknown',
    display_category: row.pantry_items?.display_category ?? 'Other',
    location:         row.pantry_items?.location         ?? null,
  }))
}

interface Section { category: string; items: FlatGuestItem[] }

function buildGuestSections(items: FlatGuestItem[]): Section[] {
  const map = new Map<string, FlatGuestItem[]>()
  for (const item of items) {
    const cat = item.display_category || 'Other'
    if (!map.has(cat)) map.set(cat, [])
    map.get(cat)!.push(item)
  }
  for (const [, group] of map) {
    group.sort((a, b) => a.sort_order - b.sort_order)
  }
  const known   = CATEGORY_ORDER.filter(c => map.has(c))
  const unknown = [...map.keys()].filter(c => !CATEGORY_ORDER.includes(c))
  return [...known, ...unknown].map(cat => ({ category: cat, items: map.get(cat)! }))
}

// ─── GuestShopper ─────────────────────────────────────────────────────────────

export default function GuestShopper() {
  const { token } = useParams<{ token: string }>()

  const [phase,   setPhase]   = useState<'loading' | 'expired' | 'active' | 'done'>('loading')
  const [session, setSession] = useState<GuestSession | null>(null)
  const [items,   setItems]   = useState<FlatGuestItem[]>([])
  const [checked, setChecked] = useState<Set<string>>(new Set())
  const [syncing, setSyncing] = useState(false)
  const confettiFired         = useRef(false)

  // ── Load session + list ────────────────────────────────────────────────────

  useEffect(() => {
    if (!token) { setPhase('expired'); return }

    async function load() {
      const { data: sess, error: sessErr } = await supabase
        .from('guest_sessions')
        .select('id, household_id, list_id, token, expires_at, used_count')
        .eq('token', token!)
        .maybeSingle()

      if (sessErr || !sess) { setPhase('expired'); return }
      if (isTokenExpired(sess.expires_at)) { setPhase('expired'); return }

      setSession(sess)

      await supabase
        .from('guest_sessions')
        .update({
          used_count:   sess.used_count + 1,
          last_used_at: new Date().toISOString(),
        })
        .eq('id', sess.id)

      const { data: rawItems, error: itemsErr } = await supabase
        .from('list_items')
        .select(`
          id, status, sort_order,
          pantry_items ( id, name, display_category, location )
        `)
        .eq('list_id', sess.list_id)
        .neq('status', 'removed')
        .order('sort_order')

      if (itemsErr) { setPhase('expired'); return }

      const flat = flattenGuestItems((rawItems ?? []) as unknown as GuestListItem[])
      setItems(flat)
      setChecked(new Set(flat.filter(i => i.status === 'checked').map(i => i.id)))
      setPhase('active')
    }

    load()
  }, [token])

  // ── Completion detection ───────────────────────────────────────────────────

  useEffect(() => {
    if (phase !== 'active' || items.length === 0) return
    const allChecked = items.every(i => checked.has(i.id))
    if (allChecked && !confettiFired.current) {
      confettiFired.current = true
      setTimeout(() => setPhase('done'), 300)
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
        // Warm-editorial palette
        colors: ['#2B4A3A', '#B5683A', '#D8B58A', '#5A7E66', '#C25B4E'],
      })
    }
  }, [checked, items, phase])

  // ── Real-time sync ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!session) return

    const channel = supabase
      .channel(`guest-list-${session.list_id}`)
      .on(
        'postgres_changes',
        {
          event:  'UPDATE',
          schema: 'public',
          table:  'list_items',
          filter: `list_id=eq.${session.list_id}`,
        },
        (payload) => {
          const updated = payload.new as { id: string; status: string }
          setItems(prev =>
            prev.map(i => i.id === updated.id ? { ...i, status: updated.status } : i)
          )
          setChecked(prev => {
            const next = new Set(prev)
            updated.status === 'checked' ? next.add(updated.id) : next.delete(updated.id)
            return next
          })
        }
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [session])

  // ── Toggle ─────────────────────────────────────────────────────────────────

  const handleToggle = useCallback(async (itemId: string) => {
    const isNowChecked = !checked.has(itemId)
    const newStatus    = isNowChecked ? 'checked' : 'pending'

    setChecked(prev => {
      const next = new Set(prev)
      isNowChecked ? next.add(itemId) : next.delete(itemId)
      return next
    })

    setSyncing(true)
    const { error } = await supabase
      .from('list_items')
      .update({ status: newStatus })
      .eq('id', itemId)

    if (error) {
      console.error('[GuestShopper] toggle failed:', error)
      setChecked(prev => {
        const next = new Set(prev)
        isNowChecked ? next.delete(itemId) : next.add(itemId)
        return next
      })
    }
    setSyncing(false)
  }, [checked])

  // ── Progress ───────────────────────────────────────────────────────────────

  const total     = items.length
  const doneCount = items.filter(i => checked.has(i.id)).length
  const pct       = total > 0 ? (doneCount / total) * 100 : 0
  const sections  = buildGuestSections(items)

  // ── Acquisition banner (used in done + active states) ─────────────────────

  function AcquisitionBanner() {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-cream pt-3 pb-6 px-5 max-w-[480px] mx-auto">
        <div className="absolute inset-x-0 -top-4 h-4 bg-gradient-to-t from-cream to-transparent pointer-events-none" />
        <div className="rounded-2xl border border-border-sand bg-paper px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-serif text-[14px] font-medium text-ink leading-tight">
              Try <em className="italic">Pantry</em> free
            </p>
            <p className="font-serif italic text-[11px] text-ink-muted mt-0.5">
              Never run out of anything again.
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.open('/', '_blank')}
            className="rounded-xl bg-forest text-forest-ink px-4 py-2 text-[12px] font-bold tracking-[-0.01em] flex-shrink-0"
          >
            Get started
          </button>
        </div>
      </div>
    )
  }

  // ── LOADING ────────────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <main className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4">
        <p className="font-serif text-[24px] text-ink tracking-[-0.015em]">
          Pantry
        </p>
        <span className="h-7 w-7 rounded-full border-2 border-forest border-t-transparent animate-spin" />
        <p className="font-serif italic text-[13px] text-ink-muted">
          Getting your list ready…
        </p>
      </main>
    )
  }

  // ── EXPIRED ────────────────────────────────────────────────────────────────

  if (phase === 'expired') {
    return (
      <main className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4 px-8 text-center">
        <div className="h-16 w-16 rounded-2xl bg-paper border border-border-sand flex items-center justify-center mb-2">
          <Link2Off className="h-7 w-7 text-terracotta" strokeWidth={1.5} />
        </div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-terracotta font-medium">
          Guest link
        </p>
        <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em]">
          This link has <em className="italic">expired</em>
        </h1>
        <p className="font-serif italic text-[13px] text-ink-muted leading-[1.5] max-w-[280px]">
          Ask the list owner to send a new one. Guest links expire after 24 hours.
        </p>
      </main>
    )
  }

  // ── DONE ───────────────────────────────────────────────────────────────────

  if (phase === 'done') {
    return (
      <main className="min-h-screen bg-cream flex flex-col items-center justify-center gap-5 px-8 text-center pb-32">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1,   opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          className="h-20 w-20 rounded-full bg-ok-bg border border-forest/30 flex items-center justify-center"
        >
          <Check className="h-10 w-10 text-forest" strokeWidth={2} />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-2">
            All done
          </p>
          <h1 className="font-serif text-[26px] text-ink leading-[1.1] tracking-[-0.015em]">
            Great <em className="italic">trip</em>.
          </h1>
          <p className="font-serif italic text-[13px] text-ink-muted mt-2">
            Everything checked off.
          </p>
        </motion.div>

        <AcquisitionBanner />
      </main>
    )
  }

  // ── ACTIVE LIST ────────────────────────────────────────────────────────────

  return (
    <main className="min-h-screen bg-cream pb-32">
      <div className="max-w-[440px] mx-auto px-5">

        {/* Header */}
        <div className="pt-3 pb-3">
          <div className="flex items-baseline justify-between">
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium">
              Pantry · Shopping list
            </p>
            <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-forest tabular-nums">
              {doneCount}/{total}
            </span>
          </div>
          <h1 className="font-serif text-[22px] text-ink leading-[1.15] tracking-[-0.015em] mt-1">
            Tap to <em className="italic">check off</em>
          </h1>
        </div>

        {/* Guest banner */}
        <div className="rounded-2xl bg-cream-warm border border-terracotta/30 px-4 py-3 mb-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-terracotta font-medium mb-0.5">
            Guest mode
          </p>
          <p className="font-serif italic text-[12px] text-ink leading-[1.4]">
            You can check off items but not add or remove them.
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-4">
          <div className="h-1.5 rounded-full overflow-hidden bg-border-sand">
            <motion.div
              className="h-full rounded-full bg-forest"
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Sync indicator */}
        <AnimatePresence>
          {syncing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center gap-1.5 mb-2"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-forest animate-pulse" />
              <span className="font-serif italic text-[11px] text-ink-muted">Syncing…</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sections */}
        {sections.map(({ category, items: sectionItems }) => (
          <div key={category}>
            <div className="flex items-baseline gap-2 mt-5 mb-2">
              <h2 className="font-serif text-[14px] italic text-ink">{category}</h2>
              <div className="flex-1 h-px bg-border-sand" />
              <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted">
                {sectionItems.length}
              </span>
            </div>

            <div className="rounded-2xl border border-border-sand bg-paper overflow-hidden">
              {sectionItems.map((item, idx) => {
                const isChecked = checked.has(item.id)
                const showLoc   = item.location && item.location !== 'pantry'
                const tint      = LOC_TINT[item.location ?? 'other'] ?? LOC_TINT.other
                return (
                  <motion.button
                    key={item.id}
                    type="button"
                    onClick={() => handleToggle(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      idx !== sectionItems.length - 1 ? 'border-b border-border-hairline' : ''
                    } ${isChecked ? 'bg-ok-bg/40' : 'bg-paper'}`}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Checkbox */}
                    <motion.div
                      className={`h-6 w-6 rounded-full flex-shrink-0 flex items-center justify-center transition ${
                        isChecked
                          ? 'bg-forest text-forest-ink'
                          : 'border-[1.5px] border-border-avatar bg-paper'
                      }`}
                      animate={{ scale: isChecked ? [1, 0.85, 1] : 1 }}
                      transition={{ duration: 0.18 }}
                    >
                      {isChecked && (
                        <motion.svg
                          initial={{ opacity: 0, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.15 }}
                          width="12" height="12" viewBox="0 0 12 12" fill="none"
                        >
                          <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </motion.svg>
                      )}
                    </motion.div>

                    {/* Name + location pill */}
                    <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                      <p
                        className={`font-serif text-[14px] font-medium leading-tight tracking-[-0.01em] ${
                          isChecked ? 'text-ink-muted line-through decoration-ink-muted' : 'text-ink'
                        }`}
                      >
                        {item.name}
                      </p>
                      {showLoc && !isChecked && (
                        <span className={`text-[10px] uppercase tracking-[0.08em] font-medium px-2 py-0.5 rounded-full ${tint}`}>
                          {item.location}
                        </span>
                      )}
                    </div>
                  </motion.button>
                )
              })}
            </div>
          </div>
        ))}

      </div>

      <AcquisitionBanner />
    </main>
  )
}
